import React from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'

import { BaseModalProps } from '@context/ModalsContext'

import { FeatureIcon } from '@ui/components/FeatureIcon/FeatureIcon'
import { Modal } from '@ui/components/Modal'
import { CircleCheckIcon } from '@ui/icons/CircleCheck.icon'

import { MasterTransactionDto } from '@model/MasterTransactionDto'
import { TransferTypeEnum } from '@model/TransactionDto'
import { WalletTypeEnum } from '@model/WalletDto'

import { useFormatNumber } from '@hooks/useFormatNumber'

interface Props extends BaseModalProps {
  masterTransaction: MasterTransactionDto | undefined
  transferType: number
  isConversion?: boolean
}

export const TransferSuccessModal: React.FC<Props> = (props) => {
  const { close, masterTransaction, isConversion, transferType } = props

  const { t } = useTranslation()

  const title = isConversion ? t('Conversion Successful') : t('Transfer Successful')

  const { formatMoney } = useFormatNumber()
  const location = useLocation()

  const isIbWallet = location.pathname.includes('introducing-broker')

  if (!masterTransaction) {
    return null
  }

  const { walletTransactions, tradingAccountTransactions } = masterTransaction

  const [fromWallet, toWallet] =
    transferType === TransferTypeEnum.TA_TO_WALLET
      ? [undefined, walletTransactions[0]]
      : walletTransactions

  const [fromTA, toTA] =
    transferType === TransferTypeEnum.WALLET_TO_TA
      ? [undefined, tradingAccountTransactions[0]]
      : tradingAccountTransactions

  const fromCurrency = fromWallet?.wallet?.currency.id || fromTA?.currency.id
  const toCurrency = toWallet?.wallet?.currency.id || toTA?.currency?.id

  const fromAmount = fromWallet?.amount || fromTA?.amount
  const toAmount = toWallet?.amount || toTA?.amount

  const isToIbWallet = toWallet?.wallet?.walletType?.id === WalletTypeEnum.IB

  const prefix = (value: string, check: boolean) => (check ? `${t('IB.IB')} ${value}` : value)

  return (
    <Modal>
      <Modal.Body centered>
        <FeatureIcon size='lg' icon={CircleCheckIcon} bg='surface-secondary-high-em' />
        <Modal.Body.Title color='text-high-em'>{title}</Modal.Body.Title>
        <Modal.Body.LabeledInfo
          label={t('Wallet.From')}
          value={prefix(
            `${fromCurrency} ${t(getTransferNameByType(transferType).from)}`,
            isIbWallet
          )}
        />

        <Modal.Body.LabeledInfo
          label={t('Wallet.Amount Sent')}
          value={formatMoney(fromAmount, fromCurrency?.toString())}
        />

        <Modal.Body.LabeledInfo
          label={t('To')}
          value={prefix(`${toCurrency} ${t(getTransferNameByType(transferType).to)}`, isToIbWallet)}
        />
        <Modal.Body.LabeledInfo
          label={t('Amount Received')}
          value={formatMoney(toAmount, toCurrency?.toString())}
        />

        {fromWallet?.currencyRate && fromWallet.currencyRate !== 1 && (
          <Modal.Body.LabeledInfo
            label={t('Wallet.Conversion Rate')}
            value={fromWallet.currencyRate.toString()}
          />
        )}
      </Modal.Body>
      <Modal.Footer>
        <Modal.Footer.Secondary onClick={close}>{t('Got it')}</Modal.Footer.Secondary>
      </Modal.Footer>
    </Modal>
  )
}

const getTransferNameByType = (transferType: number): { from: string; to: string } => {
  if (transferType === TransferTypeEnum.TA_TO_WALLET) {
    return { from: 'Trading Account.Trading Account', to: 'Wallet.Wallet' }
  }

  if (transferType === TransferTypeEnum.WALLET_TO_TA) {
    return { from: 'Wallet.Wallet', to: 'Trading Account.Trading Account' }
  }

  if (transferType === TransferTypeEnum.WALLET_TO_WALLET) {
    return { from: 'Wallet.Wallet', to: 'Wallet.Wallet' }
  }

  if (transferType === TransferTypeEnum.TA_TO_TA) {
    return {
      from: 'Trading Account.Trading Account',
      to: 'Trading Account.Trading Account',
    }
  }

  return { from: '', to: '' }
}
